import { Button, Modal, Spinner } from 'react-bootstrap';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Slide, toast } from 'react-toastify';

import ApiHelper from '../Shared/ApiHelper';
import { ReportMetadata } from './ReportModels';
import { useMsal } from '@azure/msal-react';

interface BulkDeleteModalProps {
    showModal: boolean;
    setShowModal: (show: boolean) => void;
    selectedReports: ReportMetadata[];
    refreshData: () => void | null;
    updateFavoriteReports?: () => Promise<void>;
    deleteSingleReport?: (reportId: string) => Promise<void>;
}

const BulkDeleteModal: FunctionComponent<BulkDeleteModalProps> = (props: BulkDeleteModalProps) => {
    const { showModal, setShowModal, selectedReports, refreshData, deleteSingleReport, updateFavoriteReports } = props;

    const [isDeleting, setIsDeleting] = useState(false);

    const [deletableReports, setDeletableReports] = useState<ReportMetadata[]>([]);
    const [undeletableReports, setUndeletableReports] = useState<ReportMetadata[]>([]);

    const api = useMemo(() => new ApiHelper(), []);
    const { instance } = useMsal();

    useEffect(() => {
        setDeletableReports(selectedReports.filter((report) => report.type === 'Custom'));
        setUndeletableReports(selectedReports.filter((report) => report.type !== 'Custom'));
    }, [selectedReports]);

    return (
        <Modal
            scrollable
            show={showModal}
            onHide={() => setShowModal(false)}
            centered
            data-testid="bulkDeleteModal"
        >
            <Modal.Header closeButton>
                <Modal.Title>Delete Report(s)</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <p>Deleted reports are no longer accessible to any users and cannot be restored.</p>
                    <p>Are you sure you want to delete the selected report(s)?</p>
                    <ul>
                        {deletableReports.map((report) => (
                            <li key={'report-delete-label-' + report.reportKey} data-testid={`deletableReport-${report.reportKey}`}>
                                {report.name}
                            </li>
                        ))}
                        {undeletableReports.map((report) => (
                            <li key={'report-delete-label-' + report.reportKey}  data-testid={`undeletableReport-${report.reportKey}`}>
                                {report.name + '*'}
                            </li>
                        ))}
                    </ul>
                    {undeletableReports.length >0 &&(
                        <p style={{fontStyle: 'italic'}}>
                            *Report will not be deleted because only Custom reports can be deleted.
                        </p>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={() => {
                        setShowModal(false);
                    }}
                    data-testid="cancelButton"
                >
                    Cancel
                </Button>
                <Button
                    variant="outline-danger"
                    disabled={isDeleting || !deletableReports.length}
                    data-testid="deleteButton"
                    onClick={() => {
                        setIsDeleting(true);
                        if (deleteSingleReport) {
                            // Handle single report deletion with Promise
                            deleteSingleReport(deletableReports[0].reportId)
                                .then(async () => {
                                    setShowModal(false);
                                    if(updateFavoriteReports !== undefined) await updateFavoriteReports();
                                })
                                .finally(() => {
                                    setIsDeleting(false);
                                });
                        } else {
                            // Handle bulk deletion
                            api.callApi(
                                instance,
                                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                process.env.REACT_APP_NET_API_URL + '/PbiReport/bulkdelete',
                                'DELETE',
                                JSON.stringify(deletableReports.map((report) => report.reportId))
                            ).then(async () => {
                                setShowModal(false);
                                refreshData();
                                if(updateFavoriteReports !== undefined) await updateFavoriteReports();
                                toast.success('Reports Deleted Successfully', {
                                    position: 'top-right',
                                    autoClose: 5000,
                                    transition: Slide,
                                    draggable: false,
                                    closeOnClick: false,
                                    theme: 'colored',
                                });
                            }).finally(() => {
                                setIsDeleting(false);
                            });
                        }
                    }}
                >
                    {isDeleting ? (
                        <>
                            <Spinner
                                size="sm"
                                className="button-icon-padding button-spinner"
                                animation="border"
                                role="status"
                                aria-hidden="true"
                            />
                            Deleting...
                        </>
                    ) : (
                        'Delete'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default BulkDeleteModal;
